<template>
  <div
    v-if="isProject || isManageSpaces"
    class="project-description-wrap"
  >
    <div class="description-header">
      <div class="description-title">
        {{ translate('description') }}
      </div>
      <div
        v-if="!isGuestReader"
        class="description-edit group"
        :class="{ 'hover:bg-icon-bg cursor-pointer rounded': !descriptionInputFocused }"
        @click.stop="handleInputFocus"
      >
        <em
          v-if="!descriptionInputFocused"
          class="custom-icons bg-icon-default group-hover:bg-icon-hover"
          :style="getCustomIconStyle(iconPath('edit'))"
        />
        <span
          v-if="descriptionInputFocused"
          class="input-length"
          :class="{ maxLenError: maxLenError }"
        >
          {{ length }}
        </span>
      </div>
    </div>
    <div class="description-content-wrap">
      <div
        class="project-desc-wrapper"
        :class="{ 'pb-2': descriptionInputFocused }"
      >
        <div
          v-if="!descriptionInputFocused"
          class="project-description pt"
          @click.stop="handleInputFocus"
        >
          {{ description }}
        </div>

        <AutoExpandTextarea
          v-if="descriptionInputFocused"
          ref="cardTitleInput"
          :value="description"
          class="project-description edit"
          :text-value="description"
          :focus-field="true"
          :textarea-class="'project-description'"
          :initial-height="36"
          :max-length="maxLength"
          @input="description = $event.target.value"
          @enter="projectDescInputEnter"
          @esc="projectDescInputEsc($event)"
          @handleKeypress="handleKeypress"
        />
      </div>
      <div
        v-if="descriptionInputFocused"
        class="project-desc-submit"
      >
        <ButtonCustom
          :type="'important'"
          :text="translate('save')"
          :variation="'small'"
          @click="saveDescription"
        />
        <em
          :style="getCustomIconStyle(iconPath('x'))"
          class="custom-icons close-icon"
          @click="closeEditDesc"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea';
import ButtonCustom from '@/components/elements/ButtonCustom';
import headerMixin from '@/mixins/headerMixin';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'ProjectDescriptionInfoSidebar',
  components: {
    AutoExpandTextarea,
    ButtonCustom,
  },
  mixins: [headerMixin],
  setup() {
    const { iconPath, getCustomIconStyle } = useCustomIcon();
    const { isGuestReader } = useCurrentRole();

    return { iconPath, getCustomIconStyle, isGuestReader };
  },
  data() {
    return {
      descriptionInputFocused: false,
      maxLength: 160,
      maxLenError: false,
      description: '',
    };
  },
  computed: {
    currentLength() {
      return (this.description || '').length;
    },
    length() {
      return `${this.currentLength}/${this.maxLength}`;
    },
    selectedProjectId() {
      return this.$store.getters.getSelectedProjectId;
    },
    currentDescription() {
      return this.selectedProjectId !== ''
        ? this.$store.getters.getSelectedProjectDescription
        : this.$store.getters.getCurrentProjectDescription;
    },
    teamProjects() {
      return this.$store.getters.getTeamProjects;
    },
  },
  watch: {
    currentLength(newValue) {
      if (this.maxLength === newValue) {
        this.maxLenError = true;
      }
      if (this.maxLength > newValue) {
        this.maxLenError = false;
      }
    },
    currentDescription(newVal) {
      this.description = newVal;
    },
  },
  mounted() {
    this.description = this.currentDescription;
  },
  methods: {
    handleInputFocus() {
      this.descriptionInputFocused = true;
    },
    projectDescInputEsc($event) {
      $event.preventDefault();
      this.closeEditDesc();
      $event.target.blur();
    },
    projectDescInputEnter($event) {
      this.saveDescription();
      $event.target.blur();
    },
    closeEditDesc() {
      this.description = this.currentDescription;
      this.descriptionInputFocused = false;
    },
    saveDescription() {
      this.descriptionInputFocused = false;
      if (this.description !== this.currentDescription) {
        this.$store.dispatch('updateProject', { projectData: { description: this.description } });
      }
    },
    handleKeypress($event) {
      if (this.maxLength === this.currentLength) {
        $event.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss">
.project-description-wrap {
  @apply flex flex-col;

  .description-header {
    @apply flex items-center p-6 pb-2;

    .description-title {
      @apply text-sm text-text-default font-semibold;
    }

    .description-edit {
      @apply flex ml-auto h-6 w-6 items-center justify-center;

      .input-length {
        @apply text-sm text-text-default;

        &.maxLenError {
          @apply text-red-500;
        }
      }
    }
  }

  .description-content-wrap {
    @apply flex flex-col;

    .project-desc-wrapper {
      @apply p-4 pt-0;

      .project-description {
        @apply p-2 text-sm text-text-default break-words;

        &.edit {
          @apply p-0 rounded-lg cursor-text;

          .auto-expand-textarea {
            @apply bg-standard-bg border-standard-border text-text-default;
          }
        }
      }
    }

    .project-desc-submit {
      @apply flex items-center p-6 pt-0;

      .close-icon {
        @apply ml-5 cursor-pointer h-5 w-5;
      }
    }
  }
}
</style>
